import React from 'react';
import { PathTable } from '../pathBuilder/PathTable';
import { useGetPathReportQuery } from '../services/edgeApi';
import {
  PathReportSelectionContext,
  readLastState,
} from '../utils/pathReportState';
import { useSession } from '../hooks/useSession';
import { useSearchParams } from 'react-router-dom';
import { PartialRecord } from '@/types';
import { PATH_REPORT_FILTER_KEYS } from '../modes/PathReportSidebar';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { PathReportBuildStatus } from './PathReportBuildStatus';

type Props = {
  scenario_id?: string;
  gas_date?: string;
  state_ready?: boolean; // Optional prop to indicate if state is ready, can be used for conditional rendering
};

export const PathReportPaths: React.FC<Props> = (props) => {
  const selectionCtx = React.useContext(PathReportSelectionContext);
  const lastState = React.useMemo(() => readLastState() || {}, []);
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const stateReady = props.state_ready ?? true;

  const scenarioId =
    selectionCtx?.selected?.scenario_id ??
    props.scenario_id ??
    lastState.scenario_id;
  const gasDate =
    selectionCtx?.selected?.gas_date ?? props.gas_date ?? lastState.gas_date;

  // Build query args from props/context + search params
  const queryArg = React.useMemo(() => {
    if (!scenarioId || !gasDate || !user) return undefined;
    const arg: PartialRecord<string, string | number | number[]> = {
      scenario_id: scenarioId,
      gas_date: gasDate,
      user_id: user.id,
    };
    // Add filters/sorting from URL search params
    searchParams.entries().forEach(([key, value]) => {
      // Only add known filter/sort keys, or just add all except scenario_id/gas_date
      if (
        [
          'scenario_id',
          'gas_date',
          PATH_REPORT_FILTER_KEYS.WATCHLIST_IDS,
          PATH_REPORT_FILTER_KEYS.PATH_NAME,
          PATH_REPORT_FILTER_KEYS.BUY_POOL_TOKENS,
          PATH_REPORT_FILTER_KEYS.SELL_POOL_TOKENS,
          PATH_REPORT_FILTER_KEYS.TSP_LIST_TOKENS,
          PATH_REPORT_FILTER_KEYS.RECEIPT,
          PATH_REPORT_FILTER_KEYS.DELIVERY,
          PATH_REPORT_FILTER_KEYS.SORT_BY,
          PATH_REPORT_FILTER_KEYS.ORDER,
          PATH_REPORT_FILTER_KEYS.PROFIT_GT,
          PATH_REPORT_FILTER_KEYS.PROFIT_PER_DTH_GT,
        ].includes(key)
      ) {
        // Was not handling arrays properly
        if (key === PATH_REPORT_FILTER_KEYS.WATCHLIST_IDS) {
          const all = searchParams
            .getAll(key)
            .map(Number)
            .filter((v) => !isNaN(v));
          if (all.length > 0) arg[key] = all;
        } else {
          arg[key] = value;
        }
      }
    });
    return arg;
  }, [scenarioId, gasDate, user, searchParams]);

  const {
    currentData: report,
    isError,
    refetch,
    isLoading,
    isFetching,
  } = useGetPathReportQuery(queryArg!, {
    skip: !queryArg || !stateReady,
    refetchOnMountOrArgChange: true,
  });

  if (!queryArg || !stateReady) return null;
  if (isError) {
    return (
      <div role="alert">
        Unable to load the path report.{' '}
        <button className="btn btn-link" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  }
  if (report && report.report_status !== 'READY') {
    return <PathReportBuildStatus report={report} refetch={refetch} />;
  }
  const paths = report?.paths ?? [];

  return isLoading || (!report && isFetching) ? (
    <div className="d-flex justify-content-center align-items-center h-100">
      <LoadingIndicator />
    </div>
  ) : paths?.length === 0 ? (
    <div className="d-flex justify-content-center align-items-center h-100">
      <span className="text-muted">No paths found</span>
    </div>
  ) : (
    <>
      <PathTable
        pathData={paths}
        sortable
        initialSortKey="profit"
        initialSortDir="desc"
      />
    </>
  );
};
